import type { PropertyFilterQuery, PropertyFilterToken } from "@/components/table";
import { formatPersonName } from "@/lib/formatPersonName";

export const BOOKINGS_SCOPE_DISMISS_KEY = "bookings-my-scope-dismissed";
export const LOCKED_SALES_PERSON_TOKEN_ID = "locked-sales_person";
export const SCOPE_SALES_PERSON_TOKEN_ID = "scope-sales_person";
export const SCOPE_OPENED_BY_TOKEN_ID = "scope-opened_by";
export const SCOPE_OPENED_BY_TOKEN_PREFIX = "scope-opened_by-";

export const LINKED_SCOPE_HINT =
  "Locked by your booking-scope preference. These filters show bookings where you are the sales person or the opener. Turn off “Show only my bookings” to edit them.";

export const LINKED_SCOPE_HINT_SALES =
  "Locked to your account. Sales users always see bookings where they are the sales person or the opener.";

/**
 * Today is Friday in the given IANA timezone. On Fridays the "my bookings"
 * scope is automatically disabled for ops users because a single operator
 * covers all bookings that day.
 */
export function isFridayInTz(tz = "Asia/Jerusalem"): boolean {
  try {
    const weekday = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      weekday: "short",
    }).format(new Date());
    return weekday === "Fri";
  } catch {
    return new Date().getDay() === 5;
  }
}

export function isIdentityScopeToken(token: PropertyFilterToken): boolean {
  return (
    token.id === SCOPE_SALES_PERSON_TOKEN_ID ||
    token.id === SCOPE_OPENED_BY_TOKEN_ID ||
    token.id === LOCKED_SALES_PERSON_TOKEN_ID ||
    token.id.startsWith(SCOPE_OPENED_BY_TOKEN_PREFIX)
  );
}

export function lockedSalesPersonToken(link: string): PropertyFilterToken {
  return {
    id: LOCKED_SALES_PERSON_TOKEN_ID,
    propertyKey: "sales_person",
    operator: "=",
    value: link,
    valueLabel: formatPersonName(link),
  };
}

export function buildSalesScopeToken(link: string): PropertyFilterToken {
  return {
    id: SCOPE_SALES_PERSON_TOKEN_ID,
    propertyKey: "sales_person",
    operator: "=",
    value: link,
    valueLabel: formatPersonName(link),
  };
}

/**
 * One "Opened by contains" chip for every linked opener value. Comma-separated
 * values are OR-ed by the bookings API (`= ANY(...)`).
 */
export function buildOpenedByScopeToken(
  fieldValues: string[],
): PropertyFilterToken | null {
  const values = fieldValues.map((value) => value.trim()).filter(Boolean);
  if (values.length === 0) return null;
  return {
    id: SCOPE_OPENED_BY_TOKEN_ID,
    propertyKey: "opened_by",
    operator: ":",
    value: values.join(","),
    valueLabel: values.map(formatPersonName).join(", "),
  };
}

/** @deprecated Use {@link buildOpenedByScopeToken} — one contains chip. */
export function buildOpenedByTokens(fieldValues: string[]): PropertyFilterToken[] {
  const token = buildOpenedByScopeToken(fieldValues);
  return token ? [token] : [];
}

/** Tokens for the caller's linked identity (sales and/or Open By). */
export function buildLinkedScopeTokens(input: {
  salesPersonLink?: string | null;
  openedByValues?: string[] | null;
}): PropertyFilterToken[] {
  const tokens: PropertyFilterToken[] = [];
  const sales = input.salesPersonLink?.trim();
  if (sales) tokens.push(buildSalesScopeToken(sales));
  const openedBy = buildOpenedByScopeToken(input.openedByValues ?? []);
  if (openedBy) tokens.push(openedBy);
  return tokens;
}

/** Replace any sales / opened-by tokens with the linked-identity scope. */
export function withScopeTokens(
  query: PropertyFilterQuery,
  scopeTokens: PropertyFilterToken[],
): PropertyFilterQuery {
  if (scopeTokens.length === 0) return query;
  const scopeKeys = new Set(
    scopeTokens
      .map((token) => token.propertyKey)
      .filter((key): key is string => Boolean(key)),
  );
  const others = query.tokens.filter(
    (token) =>
      !isIdentityScopeToken(token) &&
      !(token.propertyKey && scopeKeys.has(token.propertyKey)),
  );
  return { ...query, tokens: [...scopeTokens, ...others] };
}

/** Drop injected identity-scope chips so they are not AND-sent as list filters. */
export function withoutScopeTokens(
  query: PropertyFilterQuery,
): PropertyFilterQuery {
  return {
    ...query,
    tokens: query.tokens.filter((token) => !isIdentityScopeToken(token)),
  };
}

export function scopeTokensMatch(
  query: PropertyFilterQuery,
  scopeTokens: PropertyFilterToken[],
): boolean {
  if (scopeTokens.length === 0) return false;
  return scopeTokens.every((expected) =>
    query.tokens.some(
      (token) =>
        token.id === expected.id &&
        token.propertyKey === expected.propertyKey &&
        token.operator === expected.operator &&
        token.value === expected.value,
    ),
  );
}

/** Replace any sales-person tokens with the single locked link token. */
export function withLockedSalesPersonToken(
  query: PropertyFilterQuery,
  link: string,
): PropertyFilterQuery {
  return withScopeTokens(query, [lockedSalesPersonToken(link)]);
}

/** Replace any opener tokens with the caller's linked "Opened by" scope. */
export function withOpenedByScopeTokens(
  query: PropertyFilterQuery,
  scopeTokens: PropertyFilterToken[],
): PropertyFilterQuery {
  return withScopeTokens(query, scopeTokens);
}

export function queryHasOpenedByToken(query: PropertyFilterQuery): boolean {
  return query.tokens.some((token) => token.propertyKey === "opened_by");
}

function openedByTokenMatches(
  token: PropertyFilterToken,
  fieldValues: string[],
): boolean {
  if (token.propertyKey !== "opened_by") return false;
  const owned = new Set(fieldValues.map((value) => value.trim()).filter(Boolean));
  return token.value
    .split(",")
    .map((part) => part.trim())
    .some((part) => owned.has(part));
}

/** True when the query already contains a token for the user's linked identity. */
export function queryHasUserScope(
  query: PropertyFilterQuery,
  fieldValues: string[],
  salesPersonLink?: string | null,
): boolean {
  const sales = salesPersonLink?.trim();
  if (
    sales &&
    query.tokens.some(
      (token) =>
        token.propertyKey === "sales_person" && token.value.trim() === sales,
    )
  ) {
    return true;
  }
  return query.tokens.some((token) => openedByTokenMatches(token, fieldValues));
}

export function isLockedSalesPersonToken(token: PropertyFilterToken): boolean {
  return (
    token.id === LOCKED_SALES_PERSON_TOKEN_ID ||
    token.id === SCOPE_SALES_PERSON_TOKEN_ID
  );
}

/** True when the query has a user-entered search or filter (not just identity chips). */
export function queryHasUserSearch(query: PropertyFilterQuery): boolean {
  return query.tokens.some((token) => !isIdentityScopeToken(token));
}

/** Display names for the caller's sales / opened-by links, joined with “or”. */
export function formatLinkedScopeNames(input: {
  salesPersonLink?: string | null;
  openedByValues?: string[] | null;
}): string {
  const sales = input.salesPersonLink?.trim();
  const openedBy = (input.openedByValues ?? [])
    .map((value) => value.trim())
    .filter(Boolean);
  return [
    sales ? formatPersonName(sales) : null,
    openedBy.length ? openedBy.map(formatPersonName).join(", ") : null,
  ]
    .filter(Boolean)
    .join(" or ");
}
